import fs from "node:fs";
import path from "node:path";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import type { DriverTarget } from "@skaile/workspaces/core";
import { Command } from "commander";
import pc from "picocolors";
import { kindColor, kindColorPad, logErr, logInfo, S } from "../helpers.ts";

const SYNC_ICONS: Record<string, string> = {
  synced: pc.green("synced"),
  outdated: pc.yellow("outdated"),
  local: pc.cyan("local"),
  error: pc.red("error"),
  unknown: pc.dim("—"),
};

type Overview = ReturnType<AssetManager["overview"]>;

function printOverviewRepos(repos: Overview["repos"]): void {
  if (repos.length === 0) return;
  console.log();
  for (const r of repos) {
    const icon = r.error ? pc.red("!") : r.upToDate ? pc.green("✓") : pc.yellow("↓");
    const status = r.error
      ? pc.red(r.error)
      : r.upToDate
        ? pc.green("up to date")
        : pc.yellow(`${r.behind} behind`);
    console.log(`  ${icon} ${S.cmd(r.name)} ${pc.dim(`[${r.kind}]`)} ${status}`);
  }
}

function printOverviewDomains(byDomain: Overview["byDomain"]): void {
  const nameW = Math.max(4, ...[...byDomain.values()].flat().map((e) => e.name.length));
  const kindW = 8;

  for (const [domain, entries] of byDomain) {
    console.log();
    console.log(`  ${S.heading(domain)} ${pc.dim(`(${entries.length})`)}`);
    console.log(`  ${S.rule(nameW + kindW + 20)}`);
    for (const e of entries) {
      const sync = SYNC_ICONS[e.syncStatus] ?? SYNC_ICONS.unknown;
      const ver = e.version ? pc.dim(` v${e.version}`) : "";
      console.log(`  ${kindColorPad(e.kind, kindW)} ${S.cmd(e.name.padEnd(nameW))}  ${sync}${ver}`);
    }
  }
}

function showOverview(am: AssetManager): void {
  const result = am.overview();

  if (result.total === 0) {
    logInfo("No assets deployed. Run `skaile install` first.");
    return;
  }

  console.log();
  console.log(S.heading("  Asset Overview"));

  printOverviewRepos(result.repos);
  printOverviewDomains(result.byDomain);

  console.log();
  console.log(`  ${pc.dim(`${result.total} assets deployed`)}`);
  console.log();
}

async function showAsset(am: AssetManager, kind: string, name: string): Promise<void> {
  const entry = await am.info(`${kind}:${name}`);

  if (entry?.source && fs.existsSync(entry.source)) {
    console.log();
    console.log(`  ${kindColor(entry.kind)} ${S.heading(entry.name)}`);
    console.log(`  ${S.rule(40)}`);
    console.log();
    console.log(fs.readFileSync(entry.source, "utf-8"));
    return;
  }

  logErr(`Asset not found: ${kind}:${name}`);
  process.exit(1);
}

/**
 * Creates the `skaile show` command.
 *
 * With no arguments prints a deployed-asset overview (grouped by domain with
 * sync status). With `kind name` or `kind:name` arguments prints the raw
 * source content of a specific deployed asset.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/show#make-show-command
 */
export function makeShowCommand(): Command {
  return new Command("show")
    .description("Show deployed assets overview, or print asset content")
    .argument("[kind]", "Asset kind (skill, agent, prompt, flow, bundle, contract)")
    .argument("[name]", "Asset name")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--target <agent>", "Agent framework", "claude-code")
    .action(async (kind: string | undefined, name: string | undefined, opts) => {
      const am = new AssetManager({
        projectDir: path.resolve(opts.projectDir),
        driverTarget: opts.target as DriverTarget,
      });

      if (!kind) {
        // No arguments: show overview
        showOverview(am);
      } else if (!name) {
        // One argument: might be "kind:name" format
        if (kind.includes(":")) {
          const [k, n] = kind.split(":");
          await showAsset(am, k, n);
        } else {
          logErr("Usage: skaile show [kind name] or skaile show (no args for overview)");
          process.exit(1);
        }
      } else {
        await showAsset(am, kind, name);
      }
    });
}
